import {
  KNOWLEDGE_ITEM_TYPES,
  normalizeKnowledgeItemType,
} from './knowledgeItemModel';
import {
  normalizeProfilePlacementChapterId,
  normalizeProfilePlacementMode,
} from '../profileContract/profilePlacementMetadata';

const VALID_CONFIDENCE_VALUES = new Set(['high', 'medium', 'low']);
const VALID_FACT_STATUSES = new Set(['current', 'outdated']);
const VALID_PROFILE_VISIBILITY_OVERRIDES = new Set(['pin', 'hide']);

const normalizeText = (value = '') => String(value || '').trim();

// Presentation-safe lifecycle dates only - never a raw Firestore Timestamp
// reaches React. Handles Firestore Timestamp (.toDate()), Date, epoch number,
// and ISO string inputs; anything unparseable normalizes to null so callers can
// treat the field as simply "not available" rather than guessing a date.
const toIsoStringOrNull = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date.toISOString();
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return null;
};

const normalizeConfidence = (value) => {
  const confidence = normalizeText(value).toLowerCase();
  return VALID_CONFIDENCE_VALUES.has(confidence) ? confidence : '';
};

const getSourceRefs = (sourceRefs) => (
  Array.isArray(sourceRefs)
    ? sourceRefs.filter((sourceRef) => (
      sourceRef && typeof sourceRef === 'object' && !Array.isArray(sourceRef)
    ))
    : []
);

const resolveConfidence = (item, sourceRefs) => (
  normalizeConfidence(item.confidence)
  || sourceRefs.map((sourceRef) => normalizeConfidence(sourceRef.confidence)).find(Boolean)
  || normalizeConfidence(item.capturedBy?.confidence)
  || 'unknown'
);

// Temporary compatibility predicate until the product owns one shared confidence rule.
// It mirrors today's review flags and treats every non-high confidence as needing a check.
export const needsKnowledgeFactQuickCheck = (item = {}, confidence = 'unknown') => (
  item.needsReview === true
  || item.reviewState === 'needs_review'
  || item.reviewStatus === 'needs_review'
  || confidence !== 'high'
);

const normalizeFactStatus = (item = {}) => {
  const status = normalizeText(item.factStatus || item.status).toLowerCase();
  return VALID_FACT_STATUSES.has(status) ? status : 'current';
};

const normalizeProfileVisibilityOverride = (value) => {
  const override = normalizeText(value).toLowerCase();
  return VALID_PROFILE_VISIBILITY_OVERRIDES.has(override) ? override : null;
};

const normalizeResponseGuidance = (item = {}) => {
  const guidance = item.responseGuidance || item.metadata?.responseGuidance;
  if (typeof guidance === 'string') {
    const text = normalizeText(guidance);
    return text ? { text } : null;
  }
  if (!guidance || typeof guidance !== 'object' || Array.isArray(guidance)) return null;
  const text = normalizeText(guidance.text);
  if (!text) return null;
  const label = normalizeText(guidance.label);
  return label ? { label, text } : { text };
};

const isArchivedOrDiscarded = (item = {}) => (
  item.archived === true
  || normalizeText(item.status).toLowerCase() === 'archived'
  || normalizeText(item.status).toLowerCase() === 'rejected'
  || normalizeText(item.status).toLowerCase() === 'discarded'
  || normalizeText(item.reviewState).toLowerCase() === 'rejected'
  || normalizeText(item.reviewState).toLowerCase() === 'discarded'
  || normalizeText(item.reviewStatus).toLowerCase() === 'rejected'
  || normalizeText(item.reviewStatus).toLowerCase() === 'discarded'
);

export const normalizeKnowledgeFact = (item = {}) => {
  const sourceRefs = getSourceRefs(item.sourceRefs);
  const confidence = resolveConfidence(item, sourceRefs);
  const factStatus = normalizeFactStatus(item);

  return {
    id: normalizeText(item.id),
    childId: normalizeText(item.childId),
    title: normalizeText(item.title) || 'Care knowledge',
    summary: normalizeText(item.summary || item.content || item.sourceText),
    knowledgeType: normalizeKnowledgeItemType(
      item.knowledgeType || item.factType || item.bucket || KNOWLEDGE_ITEM_TYPES.OTHER
    ),
    profileField: normalizeText(
      item.profileField || item.metadata?.profileField || item.metadata?.supportProfileField
    ),
    profilePlacementMode: normalizeProfilePlacementMode(
      item.profilePlacementMode || item.metadata?.profilePlacementMode || item.metadata?.profilePlacement?.mode
    ),
    profileChapterId: normalizeProfilePlacementChapterId(
      item.profileChapterId || item.metadata?.profileChapterId || item.metadata?.profilePlacement?.chapterId
    ),
    extractionVersion: normalizeText(item.extractionVersion),
    capturedBy: item.capturedBy && typeof item.capturedBy === 'object' && !Array.isArray(item.capturedBy)
      ? item.capturedBy
      : {},
    factStatus,
    profileVisibilityOverride: normalizeProfileVisibilityOverride(item.profileVisibilityOverride),
    confidence,
    evidenceReview: item.evidenceReview || null,
    ...(item.reviewedBy && item.reviewedAt ? {reviewedBy:item.reviewedBy,reviewedByName:item.reviewedByName || '',reviewedAt:toIsoStringOrNull(item.reviewedAt)} : {}),
    needsQuickCheck: item.evidenceReview?.status === 'needs_review' || needsKnowledgeFactQuickCheck(item, confidence),
    sourceRefs,
    patternKey: normalizeText(item.patternKey),
    observationCount: Number(item.observationCount || sourceRefs.length || 0),
    hasConflictingEvidence: item.hasConflictingEvidence === true,
    responseGuidance: normalizeResponseGuidance(item),
    resolverEligible: factStatus === 'current' && !isArchivedOrDiscarded(item) && item.evidenceReview?.status !== 'needs_review',
    // Optional presentation-safe lifecycle dates for source context (e.g. "Added
    // May 2, 2026"). capturedAt is when this fact was first captured; updatedAt
    // is the last write to the doc. Neither is a confirmation/approval event -
    // there is no reliable single "confirmed" timestamp for a whole fact today,
    // so one is intentionally not fabricated here (see sourceRefs[].confirmedAt
    // for the existing per-source confirmation, which is untouched).
    capturedAt: toIsoStringOrNull(item.capturedAt),
    updatedAt: toIsoStringOrNull(item.updatedAt),
  };
};

export const normalizeKnowledgeFacts = (items = []) => (
  Array.isArray(items) ? items.map(normalizeKnowledgeFact) : []
);
